import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { WordNode } from '../types';
import { extractLastUserMessageSync, getTagsConfigForModel } from '../utils/extractLastUserMessage';
import { getModelName } from '../services/tauri-api';

interface ChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedNodePath: string | null;
  treeData: WordNode | null;
  sessionPrompt: string;
}

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  nodePath: string;
  timestamp: Date;
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({
  isOpen,
  onClose,
  selectedNodePath,
  treeData,
  sessionPrompt
}) => {
  const [modelName, setModelName] = useState<string | null>(null);

  // モデル名を取得してタグ設定を事前に取得
  useEffect(() => {
    const loadModelName = async () => {
      try {
        const response = await getModelName();
        if (response.success && response.data) {
          setModelName(response.data);
          // タグ設定を事前に取得（キャッシュに保存）
          await getTagsConfigForModel(response.data);
        }
      } catch (error) {
        console.error('モデル名の取得に失敗:', error);
      }
    };
    if (isOpen) {
      loadModelName();
    }
  }, [isOpen]);
  // ノードパスからテキストを構築する関数（Rustのbuild_text_to_nodeと同じロジック）
  const buildTextFromPath = useCallback((path: string, tree: WordNode): string => {
    if (!tree || !path) return '';
    
    const textParts: string[] = [];
    
    // ルートノードのwordを追加（「ルート」は含めない）
    if (tree.word !== 'ルート') {
      textParts.push(tree.word);
    }
    
    // 再帰的にパスを辿ってテキストを構築
    const findAndBuildText = (nodes: WordNode[], targetPath: string, textParts: string[]): boolean => {
      for (const node of nodes) {
        if (node.path === targetPath) {
          textParts.push(node.word);
          return true;
        }
        if (targetPath.startsWith(`${node.path}.`)) {
          textParts.push(node.word);
          if (findAndBuildText(node.children, targetPath, textParts)) {
            return true;
          }
          textParts.pop(); // バックトラック
        }
      }
      return false;
    };
    
    findAndBuildText(tree.children, path, textParts);
    return textParts.join('');
  }, []);

  // セッション全体の対話履歴を構築する関数
  const buildSessionConversationHistory = useMemo(() => {
    if (!treeData) return [];

    const conversations: ChatMessage[] = [];
    let messageId = 0;

    // 0.0.0.0の家系で最も深いパスを見つける関数
    const findDeepestZeroZeroZeroZeroPath = (node: WordNode, currentPath: string = ''): string => {
      let deepestPath = '';
      
      // 0.0.0.0から始まるパスのみを対象
      if (currentPath.startsWith('0.0.0.0')) {
        deepestPath = currentPath;
      }

      // 子ノードを再帰的に処理
      node.children.forEach((child, index) => {
        const childPath = currentPath === '' ? index.toString() : `${currentPath}.${index}`;
        const childDeepestPath = findDeepestZeroZeroZeroZeroPath(child, childPath);
        
        // より深いパスを選択
        if (childDeepestPath.split('.').length > deepestPath.split('.').length) {
          deepestPath = childDeepestPath;
        }
      });

      return deepestPath;
    };

    // 0.0.0.0の家系で最も深いパスを取得
    const deepestPath = findDeepestZeroZeroZeroZeroPath(treeData, '0');
    
    if (deepestPath) {
      // ユーザーメッセージ（プロンプト）- タグパースを適用
      const parsedUserMessage = extractLastUserMessageSync(sessionPrompt, modelName);
      
      conversations.push({
        id: `user-${messageId++}`,
        type: 'user',
        content: parsedUserMessage,
        nodePath: 'prompt',
        timestamp: new Date()
      });

      // AIメッセージ（最も深いパスまでの累積テキスト）
      const deepestText = buildTextFromPath(deepestPath, treeData);

      conversations.push({
        id: `assistant-${messageId++}`,
        type: 'assistant',
        content: deepestText,
        nodePath: deepestPath,
        timestamp: new Date()
      });
    }

    return conversations;
  }, [treeData, sessionPrompt, buildTextFromPath, modelName]);

  return (
    <div className={`fixed top-0 right-0 h-full w-96 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${
      isOpen ? 'translate-x-0' : 'translate-x-full'
    }`}>
      {/* ヘッダー */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-800">チャット</h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          title="チャット画面を閉じる"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* チャット内容 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {buildSessionConversationHistory.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p>セッションの対話履歴が表示されます</p>
          </div>
        ) : (
          buildSessionConversationHistory.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs px-4 py-2 rounded-lg ${
                  message.type === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                  <div className="text-sm font-medium mb-1">
                    {message.type === 'user' ? 'User' : 'AI'}
                  </div>
                <div className="text-sm whitespace-pre-wrap">
                  {message.content}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ChatDrawer;